import AsyncStorage from '@react-native-async-storage/async-storage'
import { createClient, FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js'
import { QueryClient, MutationCache } from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { createOrderedPersister } from '@/utils/react-query/persistence'
import { createThrottledFetch } from '@/utils/requestQueue'
import ErrorHandler from '@/utils/ErrorHandler'
import Toast from 'react-native-toast-message'

const throttledFetch = createThrottledFetch(fetch);

// Supabase auth storage on web — localStorage instead of AsyncStorage (IndexedDB),
// which is fragile on web and contributes to random logouts.
// SupportedStorage accepts sync or promise-returning methods; window guard keeps SSR/prerender safe.
const webStorageAdapter = {
  getItem: (key) => Promise.resolve(typeof window === 'undefined' ? null : window.localStorage.getItem(key)),
  setItem: (key, value) => Promise.resolve(typeof window === 'undefined' ? undefined : window.localStorage.setItem(key, value)),
  removeItem: (key) => Promise.resolve(typeof window === 'undefined' ? undefined : window.localStorage.removeItem(key)),
};

const supabaseUrl = process.env.EXPO_PUBLIC_DB_URL;
const supabaseKey = process.env.EXPO_PUBLIC_DB_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error(`Missing required Supabase environment variables: EXPO_PUBLIC_DB_URL=${supabaseUrl}, EXPO_PUBLIC_DB_KEY=${supabaseKey}`);
}

const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    storage: webStorageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
  },
  global: {
    fetch: throttledFetch,
  },
})


const mutationCache = new MutationCache({
  onError: (error) => {
    // Handle the error here
    console.error('Mutation error:', error);
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 1000 * 60 * 10,
    },
  },
});

// Cache-persist writes can fail on quota errors. Degrade to a one-time toast
// instead of an unhandled rejection going to Sentry. See CHURCH-APP-18Z.
let storageWarned = false;
const safeAsyncStorage = {
  getItem: (key) => AsyncStorage.getItem(key).catch(() => null),
  setItem: (key, value) => AsyncStorage.setItem(key, value).catch((e) => {
    console.warn('[asyncStoragePersister] write failed:', e?.message);
    if (!storageWarned) {
      storageWarned = true;
      Toast.show({ type: 'error', text2: 'Your device is running low on storage. Free up space so Reach can keep saving data offline.' });
    }
  }),
  removeItem: (key) => AsyncStorage.removeItem(key).catch(() => {}),
};

const asyncStoragePersister = createOrderedPersister(createAsyncStoragePersister({
  storage: safeAsyncStorage,
  key: 'reach-query-cache',
  throttleTime: 0,
}));

export const extractEdgeFunctionError = async (error, data) => {
  if (!error && data?.error) {
    const message = typeof data.error === 'string' ? data.error : data.error?.message || 'Something went wrong';
    return { message, error: new Error(message), data };
  }

  if (!error) return null;

  if (error instanceof FunctionsHttpError) {
    const status = error.context?.status;
    try {
      const text = error.context?.text ? await error.context.text() : '';
      if (!text) return { message: `Edge function returned HTTP ${status}`, error, data: null, httpStatus: status };
      try {
        const errorData = JSON.parse(text);
        return { message: errorData?.error || errorData?.message || text, error, data: errorData, httpStatus: status };
      } catch {
        return { message: text, error, data: null, httpStatus: status };
      }
    } catch {
      return { message: `Edge function returned HTTP ${status}`, error, data: null, httpStatus: status };
    }
  }

  if (error instanceof FunctionsRelayError) {
    return { message: error.message || 'Edge function relay error', error, data: null };
  }

  if (error instanceof FunctionsFetchError) {
    return { message: error.message || 'Failed to fetch edge function', error, data: null };
  }

  return { message: error.message || 'Something went wrong', error, data };
};

export const invokeEdgeFunction = async (functionName, invokeOptions, { mutation, context = {}, userFriendlyMessage, reportError = true } = {}) => {
  const { data, error } = await supabase.functions.invoke(functionName, invokeOptions);
  const extracted = await extractEdgeFunctionError(error, data);

  if (extracted) {
    const errorForHandler = new Error(extracted.message);
    const toastMessage = userFriendlyMessage || extracted.message;
    if (reportError) {
      ErrorHandler(errorForHandler, {
        mutation,
        ...context,
        error: extracted.error,
        responseData: extracted.data,
        ...(extracted.httpStatus ? { httpStatus: extracted.httpStatus } : {}),
      }, toastMessage);
    } else {
      Toast.show({ type: 'error', text2: toastMessage });
    }
    throw errorForHandler;
  }

  return data;
};

export {supabase, queryClient, asyncStoragePersister}
